import {
  SAVED_VIEW_LIMITS,
  type SavedView,
  type SectionViews
} from "@gettactic/api";

/**
 * Pure helpers over the nested `views[section][area]` map stored in the user
 * settings. They are split out from the hooks so the read-modify-write the
 * "replace the whole object" PUT forces on us stays testable.
 */

/** Views stored under one section/area, or an empty list when none exist yet. */
export const getAreaViews = (
  views: SectionViews | undefined,
  section: string,
  area: string
): SavedView[] => views?.[section]?.[area] ?? [];

/** Every view id across the whole object — ids must be unique globally, not per area. */
export const collectAllViewIds = (
  views: SectionViews | undefined
): Set<string> => {
  const ids = new Set<string>();
  for (const areas of Object.values(views ?? {})) {
    for (const areaViews of Object.values(areas)) {
      for (const view of areaViews) {
        ids.add(view.id);
      }
    }
  }
  return ids;
};

/**
 * Inserts or replaces a view in one area, leaving every other section and area
 * untouched. Returns a new object; the input is never mutated.
 */
export const upsertSavedView = (
  views: SectionViews | undefined,
  section: string,
  area: string,
  view: SavedView
): SectionViews => {
  const current = getAreaViews(views, section, area);
  const index = current.findIndex((v) => v.id === view.id);
  const nextArea =
    index === -1
      ? [...current, view]
      : current.map((v) => (v.id === view.id ? view : v));

  return {
    ...(views ?? {}),
    [section]: { ...(views?.[section] ?? {}), [area]: nextArea }
  };
};

/** Removes a view by id from one area. Unknown ids are a no-op. */
export const removeSavedView = (
  views: SectionViews | undefined,
  section: string,
  area: string,
  viewId: string
): SectionViews => ({
  ...(views ?? {}),
  [section]: {
    ...(views?.[section] ?? {}),
    [area]: getAreaViews(views, section, area).filter((v) => v.id !== viewId)
  }
});

export type SavedViewValidationError =
  | "empty_name"
  | "name_too_long"
  | "description_too_long"
  | "filters_too_large"
  | "too_many_views"
  | "duplicate_id";

/**
 * Checks every backend constraint before the PUT. The API answers any violation
 * with the same undifferentiated `invalid_view` code, so the only way to tell
 * the user which limit they hit is to check here first.
 *
 * `isNew` distinguishes creating a view (which grows the area, so the per-area
 * cap applies) from updating one in place.
 */
export const validateSavedView = (
  views: SectionViews | undefined,
  section: string,
  area: string,
  view: SavedView,
  isNew: boolean
): SavedViewValidationError | null => {
  const name = view.name.trim();
  if (name.length === 0) return "empty_name";
  if (name.length > SAVED_VIEW_LIMITS.maxNameLength) return "name_too_long";
  if (view.description.length > SAVED_VIEW_LIMITS.maxDescriptionLength) {
    return "description_too_long";
  }
  if (
    JSON.stringify(view.filters ?? {}).length >
    SAVED_VIEW_LIMITS.maxFiltersBytes
  ) {
    return "filters_too_large";
  }
  if (isNew) {
    if (
      getAreaViews(views, section, area).length >= SAVED_VIEW_LIMITS.maxPerArea
    ) {
      return "too_many_views";
    }
    if (collectAllViewIds(views).has(view.id)) return "duplicate_id";
  }
  return null;
};

/** User-facing copy for each constraint, so the caller never surfaces a raw 400. */
export const savedViewErrorMessage = (
  error: SavedViewValidationError
): string => {
  switch (error) {
    case "empty_name":
      return "Give the view a name.";
    case "name_too_long":
      return `Names can be at most ${SAVED_VIEW_LIMITS.maxNameLength} characters.`;
    case "description_too_long":
      return `Descriptions can be at most ${SAVED_VIEW_LIMITS.maxDescriptionLength} characters.`;
    case "filters_too_large":
      return "This combination of filters is too large to save. Try selecting fewer options.";
    case "too_many_views":
      return `You already have ${SAVED_VIEW_LIMITS.maxPerArea} saved views here. Delete one to save another.`;
    case "duplicate_id":
      return "That view already exists.";
  }
};
